import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createColumnHelper } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    type UnmatchedDataPoint,
    UnmatchedDataPointsListDecisionEnum,
    type UnmatchedDataPointsListRequest,
} from "../../apiClient/generated";
import { useUser } from "../../auth";
import { useUnmatchedDataApiClient } from "../../hooks";
import { SearchInput } from "../../ui/Inputs";
import { formatDate, formatDateTime } from "../../utils/dates";
import { DataTableV3UI, useDataTableV3 } from "../DataTable";
import {
    ClearFiltersButton,
    SEARCH_FILTER_KEY,
    useActiveFilters,
} from "../DataTable/ClearFiltersButton";
import { TableSettingsModal, TableSortModal } from "../DataTable/Extra";
import {
    LocationColumnModeToggle,
    LocationDisplay,
} from "../DataTable/LocationColumn";
import { type LocationFilterState, tableStateFamily } from "../DataTable/state";
import {
    DataProviderFilterV2,
    DateRangeFilter,
    DecisionFilter,
    LocationFilterV2,
    RangeFilter,
} from "../DataTable/TableFilters";
import { RateWithUncertaintyCell } from "../Emissions/Extras";
import { BulkActions } from "./BulkActions";
import { humanizeDecision } from "./decisions";

// import { BulkActions } from "./BulkActions";

const configurableColumns = [
    { id: "decision", name: "Action Taken" },
    { id: "detectedRate", name: "Detection rate" },
    { id: "concentration", name: "Concentration" },
    { id: "provider", name: "Provider" },
    { id: "detectionTimestamp", name: "Detection Date (UTC)" },
    { id: "createdAt", name: "Import Date" },
    { id: "location", name: "Location" },
    { id: "decisionTakenBy", name: "Reported by" },
    { id: "decisionTakenAt", name: "Date Time Reported" },
    { id: "bridgerEquipmentId", name: "Bridger Equipment ID" },
    { id: "bridgerEquipmentType", name: "Bridger Equipment Type" },
    { id: "bridgerEmissionLocationId", name: "Bridger Emission Location ID" },
    { id: "bridgerDetectionId", name: "Bridger Detection ID" },
    { id: "bridgerPersistence", name: "Bridger Persistence" },
    { id: "bridgerEmitterHeightFt", name: "Bridger Emitter Height (ft)" },
];

export const unmatchedDataPointTableFiltersAtom =
    atom<UnmatchedDataPointsListRequest>({});
const columnHelper = createColumnHelper<UnmatchedDataPoint>();
const tableId = "unmatched-datapoints-table";

interface ApiDataTable<T> {
    onClickRow?: (id: string | number, object: T) => void;
}

export const UnmatchedDataPointApiDataTableV2 = (
    props: ApiDataTable<UnmatchedDataPoint>,
) => {
    const [urlParams, setSearchParams] = useSearchParams();
    const apiClient = useUnmatchedDataApiClient();
    const isSuperuser = useUser().isSuperuser;
    // Set up initial search filter from URL
    const [filterSearch, setFilterSearch] = useState(
        urlParams.get("search") ?? "",
    );
    const debouncedSearch = useDebounce(filterSearch, 600);
    useEffect(() => {
        if (!urlParams.has("search")) {
            return;
        }
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete("search");
                return next;
            },
            { replace: true },
        );
    }, [urlParams, setSearchParams]);

    const baseEmissionColumns = useMemo(
        () => [
            columnHelper.accessor((row) => row.decision, {
                id: "decision",
                cell: (info) => {
                    const decision = info.getValue();
                    if (!decision) {
                        return (
                            <span className="italic text-gray-500">
                                Pending decision
                            </span>
                        );
                    }
                    return (
                        <div className="capitalize whitespace-nowrap">
                            {humanizeDecision(decision)}
                        </div>
                    );
                },
                header: () => "Action Taken",
                enableColumnFilter: true,
                // No decision ordering on the API yet.
                enableSorting: false,
                meta: {
                    customFilterWidget: DecisionFilter,
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    detectedRate: row.detectedRate,
                    detectedRateUncertainty: row.detectedRateUncertainty,
                }),
                {
                    id: "detectedRate",
                    meta: {
                        customFilterWidget: RangeFilter,
                        sortingKey: "detected_rate",
                    },
                    cell: (info) => {
                        const { detectedRate, detectedRateUncertainty } =
                            info.getValue();
                        return (
                            <RateWithUncertaintyCell
                                rate={
                                    detectedRate
                                        ? detectedRate / 1000
                                        : detectedRate
                                }
                                uncertainty={
                                    detectedRateUncertainty
                                        ? detectedRateUncertainty / 1000
                                        : detectedRateUncertainty
                                }
                                unit="kg/h"
                            />
                        );
                    },
                    header: () => "Detection rate",
                },
            ),
            columnHelper.accessor(
                (row) => ({
                    concentration: row.concentration,
                    concentrationUncertainty: row.concentrationUncertainty,
                }),
                {
                    id: "concentration",
                    cell: (info) => {
                        const { concentration, concentrationUncertainty } =
                            info.getValue();
                        return (
                            <RateWithUncertaintyCell
                                rate={concentration}
                                uncertainty={concentrationUncertainty}
                                unit="ppm*m"
                            />
                        );
                    },
                    header: () => "Concentration",
                    // No concentration filter/ordering on the API yet.
                    enableColumnFilter: false,
                    enableSorting: false,
                },
            ),
            columnHelper.accessor(
                (row) => ({
                    providerName: row.providerName,
                    secondaryDataSource: row.secondaryDataSource,
                }),
                {
                    id: "provider",
                    cell: (info) => {
                        const value = info.getValue();
                        return `${value.providerName} ${
                            value.secondaryDataSource?.trim() !== ""
                                ? "(" + value.secondaryDataSource + ")"
                                : ""
                        }`;
                    },
                    header: () => "Provider",
                    meta: {
                        customFilterWidget: DataProviderFilterV2,
                        sortingKey: "data_batch__data_provider__name",
                    },
                },
            ),
            columnHelper.accessor((row) => row.detectionTimestamp, {
                id: "detectionTimestamp",
                meta: {
                    sortingKey: "detection_timestamp",
                    filteringKey: "detectionDateRange",
                    customFilterWidget: DateRangeFilter,
                },
                cell: (info) => formatDateTime(info.renderValue()),
                header: () => "Detection Date (UTC)",
                enableColumnFilter: true,
            }),
            columnHelper.accessor("createdAt", {
                id: "createdAt",
                meta: {
                    customFilterWidget: DateRangeFilter,
                    sortingKey: "created_at__date",
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return "-";
                    }
                    return formatDate(info.getValue());
                },
                header: () => "Import Date",
                enableColumnFilter: true,
            }),
            columnHelper.accessor(
                (row) => ({
                    location: row.location,
                    presetIds: row.presetIds,
                }),
                {
                    id: "location",
                    cell: (info) => {
                        const { location: value, presetIds } = info.getValue();
                        if (!value) {
                            return "";
                        }
                        const locationFilter = info.table.options.meta?.filters
                            ?.location as LocationFilterState | undefined;
                        return (
                            <LocationDisplay
                                location={value.coordinates}
                                presets={presetIds}
                                selectedPresets={locationFilter?.publicPresets}
                                mapLinkParams={{
                                    lat: value.coordinates[1].toString(),
                                    lon: value.coordinates[0].toString(),
                                }}
                                allowCopy
                            />
                        );
                    },
                    header: () => (
                        <div className="min-w-36 flex items-center justify-start gap-2">
                            Location
                            <LocationColumnModeToggle />
                        </div>
                    ),
                    enableColumnFilter: true,
                    enableSorting: false,
                    meta: {
                        label: "Location",
                        customFilterWidget: LocationFilterV2,
                    },
                },
            ),
            columnHelper.accessor((row) => row.decisionTakenBy, {
                id: "decisionTakenBy",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Reported by",
                // No decision author filter/ordering on the API yet.
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor((row) => row.decisionTakenAt, {
                id: "decisionTakenAt",
                cell: (info) => {
                    if (!info.getValue()) {
                        return "-";
                    }
                    return formatDateTime(info.getValue());
                },
                header: () => "Date Time Reported",
                // No decision date filter/ordering on the API yet.
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor((row) => row.data?.bridger_equipment_id, {
                id: "bridgerEquipmentId",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Bridger Equipment ID",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    filteringKey: "bridgerEquipmentId",
                    sortingKey: "data__bridger_equipment_id",
                },
            }),
            columnHelper.accessor((row) => row.data?.bridger_equipment_type, {
                id: "bridgerEquipmentType",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Bridger Equipment Type",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    filteringKey: "bridgerEquipmentType",
                    sortingKey: "data__bridger_equipment_type",
                },
            }),
            columnHelper.accessor(
                (row) => row.data?.bridger_emission_location_id,
                {
                    id: "bridgerEmissionLocationId",
                    cell: (info) => info.getValue() ?? "-",
                    header: () => "Bridger Emission Location ID",
                    enableColumnFilter: true,
                    enableSorting: true,
                    meta: {
                        filteringKey: "bridgerEmissionLocationId",
                        sortingKey: "data__bridger_emission_location_id",
                    },
                },
            ),
            columnHelper.accessor((row) => row.data?.bridger_detection_id, {
                id: "bridgerDetectionId",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Bridger Detection ID",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    filteringKey: "bridgerDetectionId",
                    sortingKey: "data__bridger_detection_id",
                },
            }),
            columnHelper.accessor((row) => row.data?.bridger_persistence, {
                id: "bridgerPersistence",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Bridger Persistence",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    filteringKey: "bridgerPersistence",
                    sortingKey: "data__bridger_persistence",
                },
            }),
            columnHelper.accessor((row) => row.data?.["emitter_height_(ft)"], {
                id: "bridgerEmitterHeightFt",
                cell: (info) => info.getValue() ?? "-",
                header: () => "Bridger Emitter Height (ft)",
                enableColumnFilter: true,
                enableSorting: true,
                meta: {
                    customFilterWidget: RangeFilter,
                    filteringKey: "bridgerEmitterHeightFt",
                    sortingKey: "data__emitter_height_(ft)",
                },
            }),
        ],
        [],
    );

    // Table state
    const tableAtom = useMemo(
        () =>
            tableStateFamily({
                tableId,
                initialState: {
                    sorting: [
                        {
                            id: "createdAt",
                            desc: true,
                        },
                        {
                            id: "detectedRate",
                            desc: true,
                        },
                    ],
                    filters: {
                        decision: [UnmatchedDataPointsListDecisionEnum.Pending],
                    },
                    columnVisibility: {
                        concentration: false,
                        createdAt: false,
                        decisionTakenBy: false,
                        decisionTakenAt: false,
                        bridgerEquipmentId: false,
                        bridgerEquipmentType: false,
                        bridgerEmissionLocationId: false,
                        bridgerDetectionId: false,
                        bridgerPersistence: false,
                        bridgerEmitterHeightFt: false,
                    },
                },
            }),
        [],
    );

    // Custom reset that clears filters
    const [tableState, setTableState] = useAtom(tableAtom);
    const [filters, setFilters] = useAtom(unmatchedDataPointTableFiltersAtom);
    const resetTableState = useCallback(() => {
        setTableState((ts) => ({
            ...ts,
            sorting: [
                {
                    id: "createdAt",
                    desc: true,
                },
                {
                    id: "detectedRate",
                    desc: true,
                },
            ],
            filters: {
                decision: [UnmatchedDataPointsListDecisionEnum.Pending],
            },
            rowSelection: {},
            pagination: { ...ts.pagination, pageIndex: 0 },
        }));
    }, [setTableState]);

    const { table, dataQuery, dataName } = useDataTableV3({
        dataName: tableId,
        columns: baseEmissionColumns,
        extraFilters: useMemo(
            () => ({
                search: debouncedSearch,
            }),
            [debouncedSearch],
        ),
        fetchFunction: async (props: UnmatchedDataPointsListRequest) => {
            // Update filters variable every time the table makes a request.
            setFilters({
                ...props,
                page: undefined,
                pageSize: undefined,
            });
            return await apiClient.unmatchedDataPointsList(props);
        },
        sortable: true,
        filterable: true,
        selectable: true,
        getRowId: (e: UnmatchedDataPoint) => e.id,
    });

    const selectedRows = useMemo(() => {
        return {
            allSelected: table.getIsAllPageRowsSelected(),
            selectedIds: Object.entries(tableState.rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => id),
        };
    }, [table, tableState.rowSelection]);

    const activeFilters = useActiveFilters({
        columns: baseEmissionColumns,
        filters: tableState.filters,
        search: filterSearch,
    });

    // Clear a single filter, mirroring resetTableState but for one key.
    const clearFilter = useCallback(
        (key: string) => {
            if (key === SEARCH_FILTER_KEY) {
                setFilterSearch("");
                return;
            }
            setTableState((ts) => ({
                ...ts,
                filters: { ...ts.filters, [key]: undefined },
                pagination: { ...ts.pagination, pageIndex: 0 },
            }));
        },
        [setTableState],
    );

    return (
        <div className="flex-1 flex flex-col">
            {/* Search bar */}
            <div className="py-3 px-4 flex items-center text-sm justify-between border-b">
                <SearchInput
                    containerClassName="w-96"
                    placeholder="Search by Data Point ID, provider name..."
                    value={filterSearch}
                    onChange={(newValue) => setFilterSearch(newValue)}
                />
                {/* Detections in a survey with no owning company are attributed
                    by the operator name the provider reported, which only
                    resolves against a single company's alternative names - so
                    they are left out of the all-companies view. */}
                {isSuperuser && (
                    <span className="flex items-center gap-2 rounded-lg bg-yellow-100 px-2 py-1 text-xs text-yellow-900 shrink-0">
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="shrink-0 text-yellow-600"
                        />
                        <div>
                            <p>Non-owned data points hidden.</p>
                            <p>Sign in as a company user to view them.</p>
                        </div>
                    </span>
                )}
                <div className="flex gap-2 items-center">
                    <BulkActions
                        filters={filters}
                        selectedIds={selectedRows.selectedIds}
                        allSelected={selectedRows.allSelected}
                        onFinalizeAction={() =>
                            setTableState((ts) => ({
                                ...ts,
                                // Delect items after bulk actions finishes.
                                rowSelection: {},
                            }))
                        }
                    />
                    <ClearFiltersButton
                        activeFilters={activeFilters}
                        onClearFilter={clearFilter}
                        onClearAll={resetTableState}
                    />
                    <TableSortModal<UnmatchedDataPoint>
                        columns={baseEmissionColumns}
                        sortingState={tableState.sorting}
                        setSortingState={(newSorting) =>
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    sorting: newSorting,
                                };
                            })
                        }
                    />
                    <TableSettingsModal
                        columnVisibility={tableState.columnVisibility}
                        setColumnVisibility={(newStates) => {
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    columnVisibility: newStates,
                                };
                            });
                        }}
                        configurableColumns={configurableColumns}
                    />
                </div>
            </div>

            {/* Data table */}
            <DataTableV3UI
                table={table}
                dataName={dataName}
                columns={baseEmissionColumns}
                loadingData={dataQuery.isFetching}
                itemCount={dataQuery.data?.count || 0}
                onClickRow={props.onClickRow}
            />
        </div>
    );
};
